import type { Config } from '../config.ts';
import type { AssessmentResult, Verdict } from '../types.ts';
import { partsText, reasonParts, type FeedbackKey, type FeedbackLang } from './feedbackVoice.ts';

/**
 * Decyzja "zaliczone / powtórz" na podstawie wyniku oceny.
 *
 * Celowo NIE używamy zbiorczego PronScore jako kryterium: Azure waży w nim najniższy z
 * komponentów najmocniej, więc jedno słabe słowo potrafi zaniżyć całość i frustrować dziecko.
 * Zamiast tego: kompletność + średnia dokładność + brak słów wyraźnie błędnych.
 */
export function decide(result: AssessmentResult, cfg: Config['scoring']): Verdict {
  // transkrypt dokładnie równy zdaniu i pewny → treść jest sprawdzona; ocena głosek pojedynczych słów i kompletność z oceny wzorca są wtedy
  // wskazówką (Azure myli się przy pauzach), a nie blokadą
  const trusted = isTrusted(result, cfg);
  const scored = result.words.filter((w) => w.error !== 'Insertion');
  const weakWords = scored.filter((w) => w.accuracy < cfg.weakWordBelow).map((w) => w.word);
  const refWords = result.words.filter((w) => w.error !== 'Insertion');
  const missing = result.words.filter((w) => w.error === 'Omission').map((w) => w.word);
  const missingFirst = refWords[0]?.error === 'Omission';
  const blocking = scored.filter((w) => w.accuracy < (trusted ? Math.min(cfg.trustedWeakWordBelow, cfg.weakWordBelow) : cfg.weakWordBelow)).length;

  const omissions = result.words.filter((w) => w.error === 'Omission').length;
  const completeness = trusted ? Math.max(result.completeness, 100) : result.completeness;

  let reason: Verdict['reason'] = 'ok';
  if (completeness < cfg.minCompleteness || omissions > cfg.allowedOmissions) reason = 'incomplete';
  else if (result.accuracy < cfg.minAccuracy) reason = 'low-accuracy';
  else if (blocking > cfg.allowedWeakWords) reason = 'weak-words';
  else if (isUnclear(result, cfg)) reason = 'unclear';

  const passed = reason === 'ok';
  // gwiazdki wynikają z procentu zdania, a zaliczenie to osobna decyzja: 3★ tylko zaliczone, bez słabych słów i z dokładnością ≥ 85;
  // 2★ za zaliczenie albo za „prawie” (dokładność ≥ 70 mimo braku zaliczenia, np. brakuje jednego słowa); 1★ dla reszty
  let stars: Verdict['stars'] = 1;
  if (passed) stars = result.accuracy >= cfg.threeStarsAccuracy && weakWords.length === 0 ? 3 : 2;
  else if (result.accuracy >= cfg.twoStarsAccuracy) stars = 2;
  return { passed, stars, weakWords, missing, missingFirst, reason };
}

/** Transkrypt dokładnie równy zdaniu i pewny — patrz `decide`. */
export function isTrusted(result: AssessmentResult, cfg: Config['scoring']): boolean {
  return result.transcriptExact === true && result.transcriptConfidence !== undefined && !isUnclear(result, cfg);
}

/**
 * Zwykłe rozpoznawanie samo nie jest pewne, co usłyszało (niska pewność albo kilka prawie remisowych wersji). Brak danych (tryb zapasowy
 * z przeglądarki, samoocena) nie jest powodem do odrzucenia.
 */
const FINAL_STOPS: ReadonlySet<string> = new Set(['d', 't', 'k', 'p', 'b', 'g']);

/**
 * Końcówka zdania wyszła słabo: ostatnia głoska ostatniego słowa wzorca to zwarta (d t k p b g) z oceną poniżej progu, a rozpoznawanie nie jest
 * ponadprzeciętnie pewne. Tak wygląda słowo ucięte tuż przed końcem („fu” zamiast „food”), które rozpoznawanie samo dopowiada. Powody progów — config.ts.
 */
export function isWeakFinalStop(result: AssessmentResult, cfg: Config['scoring']): boolean {
  if (cfg.finalStopMinAccuracy <= 0) return false;
  const confidence = result.transcriptConfidence;
  if (confidence === undefined || confidence >= cfg.finalStopMaxConfidence) return false;
  const refWords = result.words.filter((w) => w.error !== 'Insertion');
  const phonemes = refWords[refWords.length - 1]?.phonemes;
  const last = phonemes?.[phonemes.length - 1];
  return last !== undefined && FINAL_STOPS.has(last.phoneme) && last.accuracy < cfg.finalStopMinAccuracy;
}

export function isUnclear(result: AssessmentResult, cfg: Config['scoring']): boolean {
  const confidence = result.transcriptConfidence;
  const ties = result.transcriptTies ?? 0;
  const lowConfidence = confidence !== undefined && confidence < cfg.minTranscriptConfidence;
  // choć jedna prawie remisowa wersja tekstu wymaga wyższej pewności (rozpoznawanie „rozdarte” między dwiema wersjami)
  const lowWithRival = confidence !== undefined && ties >= 1 && confidence < cfg.minConfidenceWithAlternative;
  const tied = ties > cfg.maxTiedAlternatives;
  return lowConfidence || lowWithRival || tied || isWeakFinalStop(result, cfg);
}

/** Komunikat po polsku — dlaczego trzeba powtórzyć (ten sam tekst czyta bot głosem ElevenLabs: lib/feedbackVoice.ts). */
export function reasonPl(v: Verdict, praise?: FeedbackKey): string {
  return reasonIn(v, 'pl', praise);
}

/** To samo w wybranym języku uwag bota (EN albo PL). */
export function reasonIn(v: Verdict, lang: FeedbackLang, praise?: FeedbackKey): string {
  return partsText(reasonParts(v, praise), lang);
}
